/**
 * GamePage - Top-level page for active game session.
 * 
 * ARCHITECTURE RULES:
 * - Pages (src/pages/) manage Socket.io connection via useSocket()
 * - Views (src/views/) consume data from stores, NO direct socket calls
 * - Hooks (src/hooks/) register Socket.io event listeners on existing connection
 * 
 * Socket.io Connection Flow:
 * 1. GamePage calls useSocket() (requires playerId from playerStore)
 * 2. Socket connects with auth: {session_id, player_id}
 * 3. Views (TVView, PhoneView) use hooks (useGameDisplay) to listen to events
 * 4. Hooks assume socket is ALREADY connected by parent Page
 */

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSocket } from '@/hooks/useSocket'
import { useSessionGuard } from '@/hooks/useSessionGuard'
import { TVView } from '@/views/TVView'
import { PhoneView } from '@/views/PhoneView'
import { ConnectionOverlay } from '@/components/low/ConnectionOverlay'
import { PixelIcon } from '@/components/low/PixelIcon'
import { usePlayerStore } from '@/stores/playerStore'
import { useGameStore } from '@/stores/gameStore'

export function GamePage() {
  const { t } = useTranslation()
  const { role, playerId, sessionCode } = usePlayerStore()
  const fetchState = useGameStore((state) => state.fetchState)
  const fetchTVState = useGameStore((state) => state.fetchTVState)
  
  // CRITICAL: Socket.io connection managed at PAGE level
  // Views MUST NOT call useSocket() - they use hooks that assume connection exists
  const { isConnected, isConnecting, error, reconnectState } = useSocket()
  
  // Watch for lifecycle redirects (game:finished / session:closed) and verify
  // session status on mount and tab wake-up
  useSessionGuard()

  // Fetch initial state when connected (REST API fallback), then keep
  // periodically reconciling via REST for the rest of the page's lifetime.
  //
  // Story 11-9 root cause: Socket.IO room subscription is asynchronous
  // relative to page mount, so ANY broadcast (game:phone:info_updated,
  // game:tv:region_updated, game:question:ask, game:question:answered, ...)
  // can independently be missed if it fires in the window before the
  // client's socket finishes (re-)joining the session room (same race class
  // as the PhoneLobby player-list fix in this story). This can happen at
  // EVERY ask_players barrier transition in a multi-phase game (info →
  // discussion → voting, ...), not just the first one — an earlier version
  // of this fix only re-armed on `currentQuestion` clearing, which covers
  // "barrier answered" but not "next barrier's game:question:ask never
  // arrived" (confirmed by evidence: mafia-flow failing at the voting
  // ActionPanel after the discussion-phase fix started working).
  //
  // Rather than trying to enumerate every WS event that could be missed,
  // periodically re-sync via REST (the same source of truth the WS events
  // themselves update) for a bounded window. This is a deliberate,
  // bounded poll — not a `waitForTimeout` sleep in a test — and it
  // self-corrects regardless of which specific broadcast was lost.
  useEffect(() => {
    if (!isConnected || !sessionCode) return
    if (!playerId) return

    let cancelled = false
    // Poll every 3s for up to 2 minutes of the page's lifetime. Long enough
    // to cover slow multi-phase flows (info/discussion/voting) under load,
    // short enough to stop once the session has clearly settled.
    const pollIntervalMs = 3000
    const maxPolls = 40

    const fetchOnce = async (): Promise<void> => {
      try {
        if (role === 'tv') {
          await fetchTVState(sessionCode, playerId)
        } else {
          await fetchState(sessionCode, playerId)
        }
      } catch (err) {
        console.error('[GamePage] REST reconciliation fetch failed:', err)
      }
    }

    console.log('[GamePage] Fetching initial state via REST API (with periodic reconciliation)')
    void fetchOnce()

    let pollCount = 0
    const intervalId = setInterval(() => {
      pollCount += 1
      if (cancelled || pollCount > maxPolls) {
        clearInterval(intervalId)
        return
      }
      void fetchOnce()
    }, pollIntervalMs)

    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [isConnected, sessionCode, playerId, role, fetchState, fetchTVState])

  // Loading state
  if (isConnecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <PixelIcon className="text-6xl mb-4 animate-pixel-blink">⏳</PixelIcon>
          <p className="text-xl">{t('game.connectingToGame')}</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <PixelIcon className="text-6xl mb-4">❌</PixelIcon>
          <p className="text-xl mb-2">{t('game.connectionError')}</p>
          <p className="text-muted-foreground">{error.message}</p>
        </div>
      </div>
    )
  }

  // Reconnect overlay — rendered before the !isConnected guard so it is reachable
  // during mid-game reconnect (isConnected=false, reconnectState='reconnecting'|'failed')
  if (reconnectState !== 'connected') {
    return (
      <>
        {role === 'tv' ? <TVView /> : <PhoneView />}
        <ConnectionOverlay state={reconnectState} />
      </>
    )
  }

  // Not connected and not in reconnect flow — unexpected state
  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <PixelIcon className="text-6xl mb-4">⚠️</PixelIcon>
          <p className="text-xl">{t('game.notConnected')}</p>
        </div>
      </div>
    )
  }

  // Render appropriate view based on player role
  // Note: role is set during join (phone) or when host creates session (tv)
  return (
    <>
      {role === 'tv' ? <TVView /> : <PhoneView />}
    </>
  )
}
